/**
 * THE SHEET drawn over Schedule in `faithfully-kept-business/design/home/Schedule.dc.html`: the page
 * dimmed behind it, the sheet risen from the foot of the screen with its handle, and its name at the
 * top. The skip or move sheet (`MoveSheet.tsx`) and the Home edit sheet (`EditHome.tsx`) are each
 * drawn in it, so a sheet is one thing wherever it opens.
 *
 * - ITS NAME AND THE LINES THAT BELONG TO IT ARE ONE HEADER. Schedule's artboard sets the visit's
 *   line right under its day, and Feedback keeps its lines in a column beside its X, so the lines a
 *   place gives as `sub` are drawn under the name in that column, never below the X. A name with
 *   nothing under it is centred on the X.
 * - A DIALOG, named by its heading, which takes the focus when it opens so its name is read out.
 * - THE PAGE BEHIND IT IS INERT while it is there, which is `Shell`'s `over`, where a place draws it.
 * - IT RISES IN THE PAGE'S COLUMN (`column.ts`): the whole width on a phone, as drawn, and over the
 *   column at a desk, with the page dimmed the whole width of the window around it, where a tap
 *   still closes it.
 * - ITS NAME KEEPS MORE THAN A WORD ON ITS LAST LINE, as the artboards' headings do.
 * - IT CLOSES on Escape, on its close button, the X drawn on Feedback, and on a tap outside it. A
 *   sheet with no way out but a tap outside it traps anybody who cannot see where outside is. Where
 *   the focus goes once it has gone is the place's to say, since the place knows what opened it.
 */
import { useEffect, useId, useRef, type ReactNode } from 'react';
import { CLOSE } from '../words.ts';
import { COLUMN } from './column.ts';

interface SheetProps {
  /** The sheet's name, drawn at its top. */
  readonly heading: ReactNode;
  /** The lines that belong to the name, drawn under it beside the X; absent when there are none. */
  readonly sub?: ReactNode;
  /** Closes the sheet, having asked nothing more. */
  readonly onClose: () => void;
  readonly children: ReactNode;
}

/** Feedback's drawn X. */
function Cross() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.25"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M5 5l10 10M15 5 5 15" />
    </svg>
  );
}

export function Sheet({ heading, sub, onClose, children }: SheetProps) {
  const headingId = useId();
  const named = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    named.current?.focus();
  }, []);

  return (
    <div className="fixed inset-0 z-20 flex flex-col justify-end">
      <div
        data-overlay
        aria-hidden="true"
        onClick={onClose}
        className="absolute inset-0 bg-surface-brand-strong/55"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.stopPropagation();
            onClose();
          }
        }}
        className={`${COLUMN} relative max-h-dvh overflow-y-auto rounded-t-card bg-surface-raised px-5 pt-3 pb-6 shadow-[0_-4px_16px_rgb(16_34_55/0.2)]`}
      >
        <div
          aria-hidden="true"
          className="mx-auto mb-4 h-1.25 w-10 rounded-full bg-border-subtle"
        />
        <div
          className={`flex justify-between gap-3 ${sub === undefined ? 'items-center' : 'items-start'}`}
        >
          <div>
            <h2
              id={headingId}
              ref={named}
              tabIndex={-1}
              className="font-serif text-h3 font-semibold text-pretty text-text-body"
            >
              {heading}
            </h2>
            {sub}
          </div>
          <button
            type="button"
            aria-label={CLOSE}
            onClick={onClose}
            className="flex size-12 shrink-0 items-center justify-center rounded-control border-2 border-border-strong bg-surface-raised text-text-brand hover:bg-surface-accent-soft"
          >
            <Cross />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
